import { isEqual } from "lodash";

import { BabylonState } from "../../state";

/**
 * Constrains the camera's zoom and draw distance.
 *
 * Zoom limits are applied from the camera's spec if given, otherwise
 * they are inferred based on the size of the camera's target object.
 *
 * Also uses fog to help the grid fade out as it approaches the draw distance.
 * Makes the scene look a lot smoother.
 */
export function constrainCamera(state: BabylonState) {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return;
  }
  camera.babylonCamera.update();
  const target = camera.spec.target;
  const targetMesh = state.entities[target.id]?.[target.mesh]?.mesh;
  const targetPath = state.entities[target.id]?.path?.mesh;

  if (targetMesh && camera.babylonCamera.targetHost !== targetMesh) {
    camera.babylonCamera.setTarget(targetMesh);
  }
  const boundingRadius = Math.max(
    ...(targetMesh?.getBoundingInfo().boundingBox.extendSizeWorld.asArray() ?? [
      1,
    ]),
  );
  const pathRadius =
    target.mesh === "path" || !targetPath
      ? boundingRadius
      : Math.max(
          ...(targetPath
            ?.getBoundingInfo()
            .boundingBox.extendSizeWorld.asArray() ?? [1]),
        );

  const minRadius =
    target.mesh === "mesh" ? boundingRadius * 2 + 0.1 : boundingRadius + 0.1;
  const maxRadius =
    target.mesh === "mesh"
      ? Math.max(boundingRadius * 1000, pathRadius * 5)
      : boundingRadius * 40;
  const zoom = {
    min: camera.spec.zoom.min ?? minRadius,
    max: camera.spec.zoom.max ?? minRadius + maxRadius,
  };
  const maxDrawDistance = target.mesh === "mesh" ? zoom.max * 10 : zoom.max * 2;
  // Formulas for fog transparency available here: https://learn.microsoft.com/en-us/previous-versions/windows/desktop/bb324452(v=vs.85)
  // Anything further than the distance where the fog becomes nearly opaque
  // is effectively not visible.
  const NEARLY_OPAQUE = 0.01;
  // Fog transparency of 0 means only fog is visible, which happens at infinite distance.
  // Therefore, solve for fog transparency close to zero to set a cutoff where drawing things
  // (like the infinite grid) doesn't make sense anymore
  state.context.scene.fogDensity =
    Math.log(1 / NEARLY_OPAQUE) / maxDrawDistance;

  const babylonCamera = camera.babylonCamera;
  babylonCamera.minZ = zoom.min > 0 ? zoom.min / 10 : 0.1;
  babylonCamera.maxZ = Math.max(10000, zoom.max * 1000);
  babylonCamera.radius = Math.max(
    Math.min(babylonCamera.radius, zoom.max),
    zoom.min,
  );

  if (!isEqual(zoom, camera.zoom)) {
    // The computed zoom ranges and drawing distances have changed.
    camera.zoom = zoom;

    // TODO: broadcast this computed value to the react state
    // to be able to show the default value in the panel settings.

    // Update the grid to draw to the right size.
    if (state.sceneConfig.grid) {
      state.sceneConfig.grid.load.state = "stale";
    }
  }
}
